import React, { useState, useCallback, useMemo, useEffect } from "react";
import Swal from "sweetalert2";
import Modal from "../../../ui/Modal";
import ModalTableInput from "../../../ui/ModalTableInput";
import Loader from "../../../ui/Loader";
import { fetchWithAuth } from "../../../../utils/api";
import {
  swalError,
  swalWarning,
} from "../../../../utils/swalAlert";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileExcel,
  faSave,
  faDownload,
  faUpload,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import * as XLSX from "xlsx";
import PhoneInput from "react-phone-input-2";
import { userCountry } from "../../../../utils/getUserCountry";

const serverUrl = `${process.env.REACT_APP_SERVER_URL}`;

// Clean phone number helper
const cleanPhone = (val) =>
  val ? String(val).replace(/[+\-\s()]/g, "") : "";

// Field change detector helper
const isFieldChanged = (field, currentVal, originalVal) => {
  if (["locked", "receiveWhatsAppMessages", "receiveEmailMessages"].includes(field)) {
    return Boolean(currentVal) !== Boolean(originalVal);
  }
  
  if (field === "phoneNumber") {
    return cleanPhone(currentVal) !== cleanPhone(originalVal);
  }

  const curr = String(currentVal || "").trim();
  const orig = String(originalVal || "").trim();
  return curr !== orig;
};

const BulkUpdateStudentsModal = ({
  isOpen,
  onClose,
  onStudentsUpdated,
  students = [], // Existing students passed from ManageStudents page
  feedbackLanguageOptions = [],
  selectedStudentIds = new Set(),
}) => {
  const [step, setStep] = useState("UPLOAD"); // UPLOAD -> EXTRACTING -> TABLE -> UPDATING
  const [excelFile, setExcelFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [rows, setRows] = useState([]);
  const [lockAllValue, setLockAllValue] = useState("no-change");
  const [feedbackLanguageAllValue, setFeedbackLanguageAllValue] = useState("no-change");
  const [currentProgress, setCurrentProgress] = useState({
    current: 0,
    total: 0,
    name: "",
  });
  const [commonPassword, setCommonPassword] = useState("");
  const [showCommonPassword, setShowCommonPassword] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (selectedStudentIds && selectedStudentIds.size > 0) {
        const selectedList = students.filter((s) => selectedStudentIds.has(s._id));
        const parsedRows = selectedList.map((originalStudent) => ({
          id: originalStudent._id,
          matched: true,
          originalStudent,
          referenceId: originalStudent.referenceId || "",
          firstName: originalStudent.firstName || "",
          lastName: originalStudent.lastName || "",
          rollNo: originalStudent.rollNo || "",
          email: originalStudent.email || "",
          phoneNumber: originalStudent.phoneNumber || "",
          feedbackLanguage: originalStudent.feedbackLanguage || "",
          locked: originalStudent.locked || false,
          receiveWhatsAppMessages: originalStudent.receiveWhatsAppMessages || false,
          receiveEmailMessages: originalStudent.receiveEmailMessages || false,
          errors: {},
        }));
        setRows(parsedRows);
        setStep("TABLE");
      } else {
        setExcelFile(null);
        setRows([]);
        setLockAllValue("no-change");
        setFeedbackLanguageAllValue("no-change");
        setStep("UPLOAD");
      }
    }
  }, [isOpen, selectedStudentIds, students]);

  // Table headers configuration
  const headers = useMemo(
    () => [
      {
        key: "referenceId",
        label: "Reference ID",
        width: "0.8fr",
        required: true,
        title: "Unique Reference ID",
      },
      {
        key: "firstName",
        label: "First Name",
        width: "0.7fr",
        required: true,
      },
      {
        key: "lastName",
        label: "Last Name",
        width: "0.7fr",
        required: true,
      },
      {
        key: "rollNo",
        label: "Roll No",
        width: "0.6fr",
        required: false,
        title: "Student Roll Number",
      },
      {
        key: "email",
        label: "Email",
        width: "1.5fr",
        required: false,
      },
      {
        key: "phoneNumber",
        label: "Phone",
        width: "1.3fr",
        required: false,
      },
      {
        key: "feedbackLanguage",
        label: "Feedback Language",
        width: "1.2fr",
        required: false,
      },
      {
        key: "locked",
        label: "Lock",
        width: "70px",
        required: false,
        title: "Block student account",
      },
      {
        key: "receiveWhatsAppMessages",
        label: "WhatsApp Msg",
        width: "80px",
        required: false,
        title: "Enable WhatsApp notifications",
      },
      {
        key: "receiveEmailMessages",
        label: "Email Msg",
        width: "80px",
        required: false,
        title: "Enable Email notifications",
      },
      { key: "actions", label: "", width: "50px", required: false },
    ],
    [],
  );

  // Handle Drag Events for file upload
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  // Handle Drop Event for file upload
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      const extension = file.name.split(".").pop().toLowerCase();
      if (["xlsx", "xls"].includes(extension)) {
        setExcelFile(file);
      } else {
        swalWarning("Please upload a valid Excel file (.xlsx or .xls)");
      }
    }
  };

  // Handle File Input Select
  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      setExcelFile(e.target.files[0]);
    }
  };

  // Remove uploaded file from step 1
  const removeFile = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setExcelFile(null);
  };

  // Excel parsing and matching logic
  const handleExtractData = async () => {
    if (!excelFile) return;

    try {
      setStep("EXTRACTING");
      // Add small delay so user can see loader
      await new Promise((resolve) => setTimeout(resolve, 800));

      const arrayBuffer = await excelFile.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: "array" });
      const worksheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[worksheetName];

      if (!worksheet) {
        swalError("No worksheet found in the Excel file.");
        setStep("UPLOAD");
        return;
      }

      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

      if (jsonData.length === 0) {
        swalWarning("No valid data found in the Excel file.");
        setStep("UPLOAD");
        return;
      }

      const headerRow = jsonData[0];
      const dataRows = jsonData.slice(1);
      const headerMap = {};
      const parsedRows = [];

      // Map headers to fields
      headerRow.forEach((cell, colNumber) => {
        const headerValue = String(cell || "")
          .toLowerCase()
          .trim();
        if (headerValue.includes("first")) headerMap[colNumber] = "firstName";
        else if (
          headerValue.includes("last") ||
          headerValue.includes("surname")
        )
          headerMap[colNumber] = "lastName";
        else if (headerValue.includes("roll"))
          headerMap[colNumber] = "rollNo";
        else if (
          headerValue.includes("reference") ||
          (headerValue.includes("id") && !headerValue.includes("email")) ||
          headerValue.includes("admission")
        )
          headerMap[colNumber] = "referenceId";
        else if (
          headerValue === "email" ||
          (headerValue.includes("email") &&
            !headerValue.includes("message") &&
            !headerValue.includes("send"))
        )
          headerMap[colNumber] = "email";
        else if (headerValue.includes("phone"))
          headerMap[colNumber] = "phoneNumber";
        else if (
          headerValue.includes("emailmessage") ||
          headerValue === "emailmessages" ||
          headerValue.includes("send email")
        )
          headerMap[colNumber] = "receiveEmailMessages";
        else if (
          headerValue.includes("whatsapp") ||
          headerValue.includes("send whatsapp")
        )
          headerMap[colNumber] = "receiveWhatsAppMessages";
        else if (headerValue.includes("language") || headerValue.includes("feedback"))
          headerMap[colNumber] = "feedbackLanguage";
        else if (headerValue.includes("lock"))
          headerMap[colNumber] = "locked";
      });

      // Parse data rows & match with current students
      dataRows.forEach((row, index) => {
        const rowData = {
          firstName: "",
          lastName: "",
          referenceId: "",
          rollNo: "",
          email: "",
          phoneNumber: "",
          feedbackLanguage: "",
          receiveEmailMessages: null,
          receiveWhatsAppMessages: null,
          locked: null,
        };

        Object.keys(headerMap).forEach((colNumber) => {
          const field = headerMap[colNumber];
          const cellValue = row[colNumber];

          if (field) {
            const rawVal = cellValue !== undefined && cellValue !== null ? String(cellValue).trim() : "";
            
            if (field === "receiveEmailMessages" || field === "receiveWhatsAppMessages" || field === "locked") {
              const val = rawVal.toLowerCase();
              if (val === "yes" || val === "true" || val === "1") {
                rowData[field] = true;
              } else if (val === "no" || val === "false" || val === "0") {
                rowData[field] = false;
              } else {
                rowData[field] = null; // Unspecified in excel
              }
            } else {
              rowData[field] = rawVal;
            }
          }
        });

        // Skip rows that don't have referenceId
        if (!rowData.referenceId) return;

        // Perform matching locally in backend-supplied student data
        const matchRefId = rowData.referenceId.toLowerCase().trim();
        const originalStudent = students.find(
          (s) => s.referenceId && s.referenceId.toLowerCase().trim() === matchRefId
        );

        if (originalStudent) {
          // Found student in institute
          parsedRows.push({
            id: originalStudent._id,
            matched: true,
            originalStudent,
            referenceId: originalStudent.referenceId, // Read only
            // Override with excel value if provided, else keep existing value
            firstName: rowData.firstName || originalStudent.firstName || "",
            lastName: rowData.lastName || originalStudent.lastName || "",
            rollNo: rowData.rollNo || originalStudent.rollNo || "",
            email: rowData.email || originalStudent.email || "",
            phoneNumber: rowData.phoneNumber || originalStudent.phoneNumber || "",
            feedbackLanguage: rowData.feedbackLanguage || originalStudent.feedbackLanguage || "",
            locked: rowData.locked !== null ? rowData.locked : originalStudent.locked,
            receiveWhatsAppMessages: rowData.receiveWhatsAppMessages !== null ? rowData.receiveWhatsAppMessages : originalStudent.receiveWhatsAppMessages,
            receiveEmailMessages: rowData.receiveEmailMessages !== null ? rowData.receiveEmailMessages : originalStudent.receiveEmailMessages,
            errors: {},
          });
        } else {
          // Not found student in institute
          parsedRows.push({
            id: `temp_${index}`,
            matched: false,
            referenceId: rowData.referenceId,
            firstName: rowData.firstName || "",
            lastName: rowData.lastName || "",
            rollNo: rowData.rollNo || "",
            email: rowData.email || "",
            phoneNumber: rowData.phoneNumber || "",
            feedbackLanguage: rowData.feedbackLanguage || "",
            locked: rowData.locked || false,
            receiveWhatsAppMessages: rowData.receiveWhatsAppMessages || false,
            receiveEmailMessages: rowData.receiveEmailMessages || false,
            errors: {
              referenceId: "Student not found in institute",
            },
          });
        }
      });

      if (parsedRows.length === 0) {
        swalWarning("No matching rows with Reference IDs found.");
        setStep("UPLOAD");
        return;
      }

      setRows(parsedRows);
      setStep("TABLE");
    } catch (err) {
      console.error("Excel parse error:", err);
      swalError("Failed to parse Excel file. Please check the format.");
      setStep("UPLOAD");
    }
  };

  // Handle row values change in the table
  const handleRowChange = useCallback((rowIndex, field, value) => {
    setRows((prev) => {
      const updated = prev.map((row, idx) => {
        if (idx === rowIndex) {
          return {
            ...row,
            [field]: value,
            errors: { ...row.errors, [field]: null },
          };
        }
        return row;
      });

      // Disable toggles if fields cleared
      if (field === "phoneNumber" && !value) {
        updated[rowIndex].receiveWhatsAppMessages = false;
      }
      if (field === "email" && !value) {
        updated[rowIndex].receiveEmailMessages = false;
      }

      // Check duplicates for all rows when any referenceId changes
      if (field === "referenceId") {
        updated.forEach((r, idx) => {
          const trimmedVal = (r.referenceId || "").trim().toLowerCase();
          if (!trimmedVal) {
            if (r.errors.referenceId === "Reference ID already exists") {
              r.errors.referenceId = null;
            }
            return;
          }

          // Check database duplicates
          const isDbDuplicate = students.some(
            (s) =>
              s._id !== r.id &&
              s.referenceId &&
              s.referenceId.trim().toLowerCase() === trimmedVal
          );

          // Check table row duplicates
          const isTableDuplicate = updated.some(
            (otherRow, otherIdx) =>
              otherIdx !== idx &&
              otherRow.referenceId &&
              otherRow.referenceId.trim().toLowerCase() === trimmedVal
          );

          if (isDbDuplicate || isTableDuplicate) {
            r.errors.referenceId = "Reference ID already exists";
          } else {
            if (r.errors.referenceId === "Reference ID already exists") {
              r.errors.referenceId = null;
            }
          }
        });
      }

      return updated;
    });

      if (field === "locked") {
        setLockAllValue("no-change");
      }
      if (field === "feedbackLanguage") {
        setFeedbackLanguageAllValue("no-change");
      }
    }, [setLockAllValue, setFeedbackLanguageAllValue, students]);

  // Remove row from verification list
  const handleRowRemove = useCallback((rowIndex) => {
    setRows((prev) => {
      if (prev.length <= 1) {
        swalWarning("At least one student row is required.");
        return prev;
      }
      const filtered = prev.filter((_, idx) => idx !== rowIndex);

      // Re-evaluate duplicates for remaining rows
      filtered.forEach((r, idx) => {
        const trimmedVal = (r.referenceId || "").trim().toLowerCase();
        if (!trimmedVal) return;

        const isDbDuplicate = students.some(
          (s) =>
            s._id !== r.id &&
            s.referenceId &&
            s.referenceId.trim().toLowerCase() === trimmedVal
        );

        const isTableDuplicate = filtered.some(
          (otherRow, otherIdx) =>
            otherIdx !== idx &&
            otherRow.referenceId &&
            otherRow.referenceId.trim().toLowerCase() === trimmedVal
        );

        if (isDbDuplicate || isTableDuplicate) {
          r.errors.referenceId = "Reference ID already exists";
        } else {
          if (r.errors.referenceId === "Reference ID already exists") {
            r.errors.referenceId = null;
          }
        }
      });

      return filtered;
    });
  }, [students]);

  // Calculate changes counts
  const stats = useMemo(() => {
    let total = rows.length;
    let matchedCount = 0;
    let notFoundCount = 0;
    let changedCount = 0;
    let unchangedCount = 0;

    const hasCommonPassword = commonPassword && commonPassword.trim().length >= 8;

    rows.forEach((row) => {
      if (!row.matched) {
        notFoundCount++;
      } else {
        matchedCount++;
        if (hasCommonPassword) {
          changedCount++;
        } else {
          // Check if any of fields are changed compared to original DB values
          const orig = row.originalStudent;
          const changedFieldsList = [
            "referenceId",
            "rollNo",
            "firstName",
            "lastName",
            "email",
            "phoneNumber",
            "feedbackLanguage",
            "locked",
            "receiveWhatsAppMessages",
            "receiveEmailMessages",
          ];
          const hasChange = changedFieldsList.some((f) =>
            isFieldChanged(f, row[f], orig[f])
          );

          if (hasChange) {
            changedCount++;
          } else {
            unchangedCount++;
          }
        }
      }
    });

    return { total, matchedCount, notFoundCount, changedCount, unchangedCount };
  }, [rows, commonPassword]);

  // Handle master Toggle Lock All
  const handleLockAllDropdownChange = (value) => {
    setLockAllValue(value);
    setRows((prev) =>
      prev.map((row) => {
        if (!row.matched) return row; // Keep unmatched rows untouched
        let newLocked = row.locked;
        if (value === "yes") {
          newLocked = true;
        } else if (value === "no") {
          newLocked = false;
        } else if (value === "no-change") {
          newLocked = row.originalStudent.locked;
        }
        return {
          ...row,
          locked: newLocked,
        };
      })
    );
  };

  // Handle master Feedback Language Change
  const handleFeedbackLanguageAllDropdownChange = (value) => {
    setFeedbackLanguageAllValue(value);
    setRows((prev) =>
      prev.map((row) => {
        if (!row.matched) return row; // Keep unmatched rows untouched
        let newLang = row.feedbackLanguage;
        if (value === "no-change") {
          newLang = row.originalStudent.feedbackLanguage || "";
        } else {
          newLang = value;
        }
        return {
          ...row,
          feedbackLanguage: newLang,
        };
      })
    );
  };

  // Download template function
  const downloadTemplate = () => {
    const link = document.createElement("a");
    link.href = "/files/student_list_template.xlsx";
    link.download = "student_list_template.xlsx";
    link.click();
  };

  // Close and reset modal
  const handleClose = () => {
    if (step === "UPDATING") return;
    setExcelFile(null);
    setRows([]);
    setLockAllValue("no-change");
    setFeedbackLanguageAllValue("no-change");
    setCommonPassword("");
    setShowCommonPassword(false);
    setStep("UPLOAD");
    onClose();
  };

  // Handle saving of all bulk updates
  const handleSaveAll = async () => {
    if (commonPassword && commonPassword.trim().length < 8) {
      swalWarning("Password must be at least 8 characters.");
      return;
    }

    const hasCommonPassword = commonPassword && commonPassword.trim().length >= 8;

    // Filter only matched students that have at least one change (or all matched if password is set)
    const studentsToUpdate = rows.filter((row) => {
      if (!row.matched) return false;
      if (hasCommonPassword) return true;

      const orig = row.originalStudent;
      const fields = [
        "referenceId",
        "rollNo",
        "firstName",
        "lastName",
        "email",
        "phoneNumber",
        "feedbackLanguage",
        "locked",
        "receiveWhatsAppMessages",
        "receiveEmailMessages",
      ];
      return fields.some((f) => isFieldChanged(f, row[f], orig[f]));
    });

    if (studentsToUpdate.length === 0) {
      swalWarning("No student modifications detected. Nothing to update.");
      return;
    }

    // Validate email patterns and reference IDs for rows to update
    let isValidationError = false;
    const validatedRows = rows.map((row, idx) => {
      const errors = { ...row.errors };
      if (row.matched) {
        if (!row.referenceId || !row.referenceId.trim()) {
          errors.referenceId = "Required";
          isValidationError = true;
        } else {
          // Check duplicate reference ID
          const trimmedVal = row.referenceId.trim().toLowerCase();
          const isDbDuplicate = students.some(
            (s) =>
              s._id !== row.id &&
              s.referenceId &&
              s.referenceId.trim().toLowerCase() === trimmedVal
          );
          const isTableDuplicate = rows.some(
            (otherRow, otherIdx) =>
              otherIdx !== idx &&
              otherRow.referenceId &&
              otherRow.referenceId.trim().toLowerCase() === trimmedVal
          );
          if (isDbDuplicate || isTableDuplicate) {
            errors.referenceId = "Reference ID already exists";
            isValidationError = true;
          }
        }
        if (!row.firstName.trim()) {
          errors.firstName = "Required";
          isValidationError = true;
        }
        if (!row.lastName.trim()) {
          errors.lastName = "Required";
          isValidationError = true;
        }
        if (row.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email)) {
          errors.email = "Invalid email";
          isValidationError = true;
        }
      }
      return { ...row, errors };
    });

    if (isValidationError) {
      setRows(validatedRows);
      swalWarning("Please fix validation errors before updating.");
      // Scroll to error
      setTimeout(() => {
        const errorEl = document.querySelector(".table-validation-error, .has-error");
        if (errorEl) {
          errorEl.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 100);
      return;
    }

    setStep("UPDATING");
    setCurrentProgress({ current: 0, total: studentsToUpdate.length, name: "" });

    const successRecords = [];
    const errorRecords = [];

    for (let i = 0; i < studentsToUpdate.length; i++) {
      const row = studentsToUpdate[i];
      const orig = row.originalStudent;
      const fullName = `${row.firstName} ${row.lastName}`;
      
      setCurrentProgress({
        current: i + 1,
        total: studentsToUpdate.length,
        name: fullName,
      });

      // Compute payload of changed fields (or send full payload as editStudent does)
      const payload = {
        firstName: row.firstName.trim(),
        lastName: row.lastName.trim(),
        referenceId: row.referenceId.trim(),
        rollNo: row.rollNo ? row.rollNo.trim() : null,
        email: row.email.trim() || null,
        phoneNumber: cleanPhone(row.phoneNumber),
        receiveWhatsAppMessages: row.receiveWhatsAppMessages,
        receiveEmailMessages: row.receiveEmailMessages,
        batch: orig.batch ? Number(orig.batch) : null,
        level: orig.level || null,
        feedbackLanguage: row.feedbackLanguage || null,
      };

      if (commonPassword && commonPassword.trim().length >= 8) {
        payload.password = commonPassword.trim();
      }

      // Only send lock value to backend if the lock state was explicitly changed
      if (isFieldChanged("locked", row.locked, orig.locked)) {
        payload.locked = row.locked;
      }

      try {
        const response = await fetchWithAuth(
          `${serverUrl}/api/user/update/${row.id}`,
          {
            method: "PUT",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          }
        );

        if (!response) continue;

        if (response.ok) {
          successRecords.push({
            firstName: row.firstName,
            lastName: row.lastName,
            referenceId: row.referenceId,
            rollNo: row.rollNo || "-",
            email: row.email || "-",
          });
        } else {
          const errorData = await response.json().catch(() => ({}));
          errorRecords.push({
            firstName: row.firstName,
            lastName: row.lastName,
            referenceId: row.referenceId,
            rollNo: row.rollNo || "-",
            email: row.email || "-",
            error: errorData.message || "Failed to update",
          });
        }
      } catch (err) {
        errorRecords.push({
          firstName: row.firstName,
          lastName: row.lastName,
          referenceId: row.referenceId,
          rollNo: row.rollNo || "-",
          email: row.email || "-",
          error: err.message || "Network error",
        });
      }

      // 100ms delay for updates
      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    setStep("UPLOAD");
    setExcelFile(null);
    setRows([]);
    setLockAllValue("no-change");
    setFeedbackLanguageAllValue("no-change");
    setCommonPassword("");
    setShowCommonPassword(false);

    // Show summary popup
    if (successRecords.length > 0 || errorRecords.length > 0) {
      const successRowsHtml = successRecords
        .map(
          (s) =>
            `<tr style="background: #d4edda;">
              <td style="padding: 8px; border: 1px solid #c3e6cb; color: #155724;">✓</td>
              <td style="padding: 8px; border: 1px solid #c3e6cb;">${s.firstName} ${s.lastName}</td>
              <td style="padding: 8px; border: 1px solid #c3e6cb;">${s.referenceId || "-"}</td>
              <td style="padding: 8px; border: 1px solid #c3e6cb;">${s.rollNo || "-"}</td>
              <td style="padding: 8px; border: 1px solid #c3e6cb; color: #155724; font-weight: 600;">Updated</td>
            </tr>`
        )
        .join("");

      const errorRowsHtml = errorRecords
        .map(
          (s) =>
            `<tr style="background: #f8d7da;">
              <td style="padding: 8px; border: 1px solid #f5c6cb; color: #721c24;">✗</td>
              <td style="padding: 8px; border: 1px solid #f5c6cb;">${s.firstName} ${s.lastName}</td>
              <td style="padding: 8px; border: 1px solid #f5c6cb;">${s.referenceId || "-"}</td>
              <td style="padding: 8px; border: 1px solid #f5c6cb;">${s.rollNo || "-"}</td>
              <td style="padding: 8px; border: 1px solid #f5c6cb; color: #721c24;">${s.error}</td>
            </tr>`
        )
        .join("");

      const tableHtml = `
        <div style="max-height: 400px; overflow: auto; margin-top: 10px;">
          <table style="width: 100%; border-collapse: collapse; font-size: 0.85em; text-align: left;">
            <thead>
              <tr style="background: #f8f9fa;">
                <th style="padding: 8px; border: 1px solid #dee2e6; width: 30px;"></th>
                <th style="padding: 8px; border: 1px solid #dee2e6;">Name</th>
                <th style="padding: 8px; border: 1px solid #dee2e6;">Reference ID</th>
                <th style="padding: 8px; border: 1px solid #dee2e6;">Roll No</th>
                <th style="padding: 8px; border: 1px solid #dee2e6;">Status / Error</th>
              </tr>
            </thead>
            <tbody>
              ${successRowsHtml}
              ${errorRowsHtml}
            </tbody>
          </table>
        </div>
        <div style="margin-top: 12px; display: flex; gap: 20px; justify-content: center;">
          <span style="color: #155724; font-weight: 600;">✓ Updated: ${successRecords.length}</span>
          <span style="color: #721c24; font-weight: 600;">✗ Failed: ${errorRecords.length}</span>
        </div>
      `;

      Swal.fire({
        icon: errorRecords.length === 0 ? "success" : "info",
        title: errorRecords.length === 0 ? "All Students Updated!" : "Bulk Update Results Summary",
        html: tableHtml,
        width: "80%",
        confirmButtonText: "Got it!",
      });

      if (successRecords.length > 0) {
        onStudentsUpdated?.();
      }
    }
    onClose();
  };

  // Custom Cell Renderer inside ModalTableInput to support visual indications of modifications
  const renderCell = useCallback(
    (header, row, rowIndex) => {
      const val = row[header.key];
      const error = row.errors?.[header.key];
      const hasError = !!error;

      // Check if this specific field has changed from DB values
      const isChanged = row.matched && isFieldChanged(header.key, val, row.originalStudent[header.key]);

      // Locked, receiveWhatsAppMessages, receiveEmailMessages toggles
      if (["locked", "receiveWhatsAppMessages", "receiveEmailMessages"].includes(header.key)) {
        const isDisabled =
          (header.key === "receiveWhatsAppMessages" && !row.phoneNumber) ||
          (header.key === "receiveEmailMessages" && !row.email) ||
          !row.matched;

        const dbVal = row.matched ? row.originalStudent[header.key] : false;

        return (
          <div className={`toggle-diff-container ${isChanged ? "changed" : ""}`}>
            <label className="table-cell-toggle">
              <input
                type="checkbox"
                checked={val || false}
                onChange={(e) =>
                  handleRowChange(rowIndex, header.key, e.target.checked)
                }
                disabled={isDisabled}
              />
              <span className="toggle-slider"></span>
            </label>
            {isChanged && (
              <span className="toggle-diff-original-value">
                {dbVal ? "Yes" : "No"}
              </span>
            )}
          </div>
        );
      }

      // Phone Input Column
      if (header.key === "phoneNumber") {
        const dbVal = row.matched ? row.originalStudent.phoneNumber : "";
        return (
          <div className={`table-input-wrapper table-phone-wrapper ${hasError ? "has-error" : ""} cell-diff-container`}>
            <PhoneInput
              country={userCountry}
              value={val || ""}
              onChange={(value) => handleRowChange(rowIndex, "phoneNumber", value)}
              disabled={!row.matched}
              inputStyle={{
                width: "100%",
                height: "36px",
                fontSize: "14px",
                paddingLeft: "42px",
                border: hasError ? "2px solid #ef4444" : isChanged ? "2px solid #f59e0b" : "2px solid #e2e8f0",
                backgroundColor: isChanged ? "#fffbeb" : "white",
                borderRadius: "6px",
              }}
              buttonStyle={{
                height: "36px",
                border: hasError ? "2px solid #ef4444" : isChanged ? "2px solid #f59e0b" : "2px solid #e2e8f0",
                backgroundColor: isChanged ? "#fffbeb" : "white",
                borderRadius: "6px 0 0 6px",
              }}
              containerStyle={{
                width: "100%",
              }}
              dropdownStyle={{
                textAlign: "left",
                width: "inherit",
              }}
              enableSearch={true}
              disableSearchIcon={true}
              placeholder="Phone number"
            />
            {hasError && <span className="table-validation-error">{error}</span>}
            {isChanged && (
              <span className="cell-original-value">
                {dbVal || ""}
              </span>
            )}
          </div>
        );
      }

      // Feedback Language Column
      if (header.key === "feedbackLanguage") {
        const dbVal = row.matched ? row.originalStudent.feedbackLanguage : "";
        return (
          <div className="table-input-wrapper cell-diff-container">
            <select
              value={val || ""}
              onChange={(e) => handleRowChange(rowIndex, "feedbackLanguage", e.target.value)}
              disabled={!row.matched}
              className={`modal-table-input ${isChanged ? "cell-changed" : ""}`}
              style={{
                height: "36px",
                fontSize: "14px",
                padding: "0 8px",
                border: isChanged ? "2px solid #f59e0b" : "2px solid #e2e8f0",
                backgroundColor: isChanged ? "#fffbeb" : "white",
                borderRadius: "6px",
                width: "100%",
                outline: "none",
                cursor: "pointer",
                appearance: "auto",
              }}
            >
              <option value="">Select Language...</option>
              {feedbackLanguageOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {isChanged && (
              <span className="cell-original-value" style={{ textDecoration: "line-through" }}>
                {dbVal || "(empty)"}
              </span>
            )}
          </div>
        );
      }

      // Actions Remove Row Button
      if (header.key === "actions") {
        return (
          <button
            type="button"
            className="modal-table-remove-btn"
            onClick={() => handleRowRemove(rowIndex)}
            title="Remove row"
          >
            <i className="fa-solid fa-trash"></i>
          </button>
        );
      }

      // Text Fields: First Name, Last Name, Email, Reference ID
      const dbVal = row.matched ? row.originalStudent[header.key] : "";
      
      return (
        <div className={`table-input-wrapper ${hasError ? "has-error" : ""} cell-diff-container`}>
          <input
            type={header.key === "email" ? "email" : "text"}
            className={`modal-table-input ${hasError ? "input-error" : ""} ${isChanged ? "cell-changed" : ""}`}
            value={val || ""}
            onChange={(e) =>
              handleRowChange(rowIndex, header.key, e.target.value)
            }
            placeholder={header.placeholder || ""}
            disabled={!row.matched}
          />
          {hasError && <span className="table-validation-error">{error}</span>}
          {isChanged && (
            <span className="cell-original-value">
              {dbVal || ""}
            </span>
          )}
        </div>
      );
    },
    [handleRowChange, handleRowRemove, feedbackLanguageOptions]
  );

  // Compute dynamic modal width
  const modalWidth = useMemo(() => {
    if (step === "UPLOAD" || step === "LOADING") return "550px";
    if (step === "TABLE") return "95%";
    if (step === "UPDATING") return "500px";
    return "550px";
  }, [step]);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} maxWidth={modalWidth}>
      {step === "UPLOAD" && (
        <>
          <div style={{ padding: "0.5rem" }}>
            <div className="add-students-header" style={{ marginBottom: "1rem" }}>
              <div className="header-left" style={{ flex: 1, minWidth: "250px" }}>
                <h2>Bulk Update Students</h2>
                <p className="modal-description">
                  Upload an Excel file to update existing students. Matching will be performed based on the Student's <strong>Reference ID</strong>.
                </p>
              </div>
              <div className="header-right" style={{ alignSelf: "flex-end" }}>
                <button
                  type="button"
                  className="template-download-link"
                  onClick={downloadTemplate}
                  style={{ fontSize: "0.85rem" }}
                >
                  <FontAwesomeIcon icon={faDownload} /> Don't have a file? Download template
                </button>
              </div>
            </div>

            {/* Drag & Drop Zone */}
            <div
              className={`bulk-update-upload-zone ${dragActive ? "drag-active" : ""}`}
              onDragEnter={handleDrag}
              onDragOver={handleDrag}
              onDragLeave={handleDrag}
              onDrop={handleDrop}
              onClick={() => document.getElementById("bulk-update-file-picker").click()}
            >
              <input
                id="bulk-update-file-picker"
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                style={{ display: "none" }}
              />
              <FontAwesomeIcon icon={faUpload} className="bulk-update-upload-icon" />
              <div className="bulk-update-upload-text">
                <h4>Drag and drop Excel file here</h4>
                <p>or click to browse from device (.xlsx, .xls)</p>
              </div>
            </div>

            {/* Uploaded File Info */}
            {excelFile && (
              <div className="bulk-update-file-info">
                <div className="bulk-update-file-details">
                  <FontAwesomeIcon icon={faFileExcel} style={{ fontSize: "1.1rem" }} />
                  <span>{excelFile.name} ({(excelFile.size / 1024).toFixed(1)} KB)</span>
                </div>
                <button className="bulk-update-file-remove" onClick={removeFile}>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            )}

            {/* Footer Actions */}
            <div style={{ marginTop: "1rem", display: "flex", justifyContent: "flex-end" }}>
              <button
                type="button"
                className="create-btn"
                onClick={handleExtractData}
                disabled={!excelFile}
                style={{ margin: 0 }}
              >
                Extract Data
              </button>
            </div>
          </div>
        </>
      )}

      {step === "EXTRACTING" && (
        <div style={{ textAlign: "center", padding: "3rem 1.5rem" }}>
          <Loader style={{ fontSize: "2.5rem", position: "relative", left: "0", top: "0", marginBottom: "1rem" }} />
          <h3>Parsing Excel Data...</h3>
          <p style={{ color: "#64748b", margin: 0 }}>Matching student profiles with Reference IDs</p>
        </div>
      )}

      {step === "TABLE" && (
        <>
          {/* Header */}
          <div className="add-students-header" style={{ marginBottom: "1rem" }}>
            <div>
              <h2 style={{ margin: 0 }}>Bulk Update Student Profiles</h2>
              <p className="modal-description" style={{ margin: "0.25rem 0 0 0" }}>
                Verify comparisons below. Highlighted fields show pending updates from Excel.
              </p>
            </div>
          </div>

          {/* Common Password Input */}
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.75rem",
              padding: "0.5rem 0.85rem",
              backgroundColor: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              boxShadow: "0 1px 2px rgba(0, 0, 0, 0.02)",
              marginBottom: "1rem",
            }}
          >
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.4rem",
                fontSize: "0.85rem",
                fontWeight: 600,
                color: "#475569",
                whiteSpace: "nowrap",
              }}
            >
              <i className="fa-solid fa-lock" style={{ color: "#64748b", fontSize: "0.8rem" }}></i>
              Change Password:
            </label>
            <div style={{ position: "relative", width: "240px" }}>
              <input
                type={showCommonPassword ? "text" : "password"}
                value={commonPassword}
                onChange={(e) => setCommonPassword(e.target.value)}
                placeholder="Leave empty if not needed"
                autoComplete="new-password"
                style={{
                  height: "32px",
                  fontSize: "0.85rem",
                  padding: "0 32px 0 10px",
                  border: commonPassword && commonPassword.length < 8 ? "1px solid #ef4444" : "1px solid #cbd5e1",
                  borderRadius: "6px",
                  width: "100%",
                  backgroundColor: "#ffffff",
                  outline: "none",
                  boxSizing: "border-box",
                  transition: "all 0.2s ease",
                }}
              />
              <button
                type="button"
                onClick={() => setShowCommonPassword((prev) => !prev)}
                style={{
                  position: "absolute",
                  right: "8px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "#94a3b8",
                  padding: "4px",
                  fontSize: "12px",
                  display: "flex",
                  alignItems: "center",
                }}
                title={showCommonPassword ? "Hide password" : "Show password"}
              >
                <i className={`fa-solid ${showCommonPassword ? "fa-eye-slash" : "fa-eye"}`}></i>
              </button>
            </div>
          </div>

          {/* Stats Summary Bar */}
          <div className="bulk-update-stats-bar" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "1rem" }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "1.5rem" }}>
              <div className="bulk-update-stat-item" style={{ color: "#475569" }}>
                <span>Total rows:</span> <strong>{stats.total}</strong>
              </div>
              <div className="bulk-update-stat-item" style={{ color: "#166534" }}>
                <span className="status-badge ready">Matched & Changed:</span> <strong>{stats.changedCount}</strong>
              </div>
              <div className="bulk-update-stat-item" style={{ color: "#64748b" }}>
                <span className="status-badge no-change">Unchanged:</span> <strong>{stats.unchangedCount}</strong>
              </div>
              {stats.notFoundCount > 0 && (
                <div className="bulk-update-stat-item" style={{ color: "#991b1b" }}>
                  <span className="status-badge error">Not Found (will be skipped):</span> <strong>{stats.notFoundCount}</strong>
                </div>
              )}
            </div>
            
            <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
              {/* Feedback Language All Dropdown */}
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <span style={{ fontSize: "0.875rem", fontWeight: "600", color: "#475569" }}>Feedback Language:</span>
                <select
                  value={feedbackLanguageAllValue}
                  onChange={(e) => handleFeedbackLanguageAllDropdownChange(e.target.value)}
                  style={{
                    padding: "0.35rem 1.8rem 0.35rem 0.75rem",
                    fontSize: "0.875rem",
                    fontWeight: "600",
                    color: "#475569",
                    border: "1px solid #cbd5e1",
                    borderRadius: "6px",
                    backgroundColor: "white",
                    outline: "none",
                    cursor: "pointer",
                    appearance: "auto",
                  }}
                >
                  <option value="no-change">No Change</option>
                  {feedbackLanguageOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Lock All Dropdown */}
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <span style={{ fontSize: "0.875rem", fontWeight: "600", color: "#475569" }}>Lock All:</span>
                <select
                  value={lockAllValue}
                  onChange={(e) => handleLockAllDropdownChange(e.target.value)}
                  style={{
                    padding: "0.35rem 1.8rem 0.35rem 0.75rem",
                    fontSize: "0.875rem",
                    fontWeight: "600",
                    color: "#475569",
                    border: "1px solid #cbd5e1",
                    borderRadius: "6px",
                    backgroundColor: "white",
                    outline: "none",
                    cursor: "pointer",
                    appearance: "auto",
                  }}
                >
                  <option value="no-change">No Change</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
            </div>
          </div>

          {/* Table Body */}
          <ModalTableInput
            headers={headers}
            rows={rows}
            onRowChange={handleRowChange}
            onRowRemove={handleRowRemove}
            renderCell={renderCell}
            addButtonText="" // Disable manually adding rows
            onRowAdd={null} // Disable manually adding rows
            footerContent={
              <span className="row-count">
                {stats.changedCount} student{stats.changedCount !== 1 ? "s" : ""} to update
              </span>
            }
          />

          {/* Footer Actions */}
          <div className="add-students-footer">
            <button
              type="button"
              className="create-btn"
              onClick={handleSaveAll}
              disabled={stats.changedCount === 0}
              style={{ margin: 0 }}
            >
              <FontAwesomeIcon icon={faSave} /> Update All ({stats.changedCount})
            </button>
          </div>
        </>
      )}

      {step === "UPDATING" && (
        <div className="bulk-progress-overlay" style={{ position: "relative", minHeight: "220px", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div className="progress-content" style={{ width: "100%", padding: "1.5rem" }}>
            <Loader style={{ fontSize: "2.5rem", left: "0", top: "0", display: "block", margin: "0 auto 1rem auto", position: "relative" }} />
            <h3 style={{ textAlign: "center", margin: "0 0 0.5rem 0" }}>Updating Student Profiles...</h3>
            <p className="progress-text" style={{ textAlign: "center", margin: "0 0 0.25rem 0" }}>
              Updating <strong>{currentProgress.name}</strong>
            </p>
            <p className="progress-count" style={{ textAlign: "center", fontWeight: "600", color: "#64748b", fontSize: "0.9rem", margin: "0 0 1rem 0" }}>
              {currentProgress.current} of {currentProgress.total} completed
            </p>
            <div className="progress-bar-container" style={{ height: "8px", backgroundColor: "#e2e8f0", borderRadius: "4px", overflow: "hidden" }}>
              <div
                className="progress-bar-fill"
                style={{
                  height: "100%",
                  backgroundColor: "#10b981",
                  width: `${(currentProgress.current / currentProgress.total) * 100}%`,
                  transition: "width 0.1s ease-out",
                }}
              />
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default BulkUpdateStudentsModal;
